import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ExternalLink,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import type { HighlightDigestDTO, HighlightItem } from "@app/shared";
import { api } from "../lib/api";
import { useActiveBrand } from "../lib/brand";
import { formatDateTime } from "../lib/format";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { DraftsList } from "./DraftsList";
import {
  ImageSourceSelect,
  useImageSourceState,
} from "../components/ImageSourceSelect";
import { useToast } from "../components/Toast";

/** Format a 0..1 relevance score as a whole-number percentage. */
function formatScore(score: number): string {
  return `${Math.round(score * 100)}%`;
}

/**
 * Special Case: per-account daily highlight digest. The operator refreshes
 * highlights (enqueues the research job), reviews each item with its relevance
 * score + risk flag, and manually generates a draft from a chosen item. Produced
 * drafts are listed below via the shared DraftsList in `special` mode.
 */
export function SpecialCase() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const accountKey = useActiveBrand();
  const [imageSource, setImageSource] = useImageSourceState();
  // The research job runs ~30-60s in the background; while a manual refresh is in
  // flight we poll the digest list until a newer digest replaces the current one.
  const [awaitingRefresh, setAwaitingRefresh] = useState(false);
  const baselineId = useRef<string | null>(null);
  const giveUp = useRef<ReturnType<typeof setTimeout> | null>(null);

  const digestsQuery = useQuery({
    queryKey: ["highlights", accountKey],
    queryFn: ({ signal }) => api.highlights({ accountKey: accountKey! }, signal),
    enabled: !!accountKey,
    refetchInterval: awaitingRefresh ? 4000 : false,
  });

  const digests = digestsQuery.data ?? [];
  // The list is returned latest-first; the freshest digest is the head.
  const latest: HighlightDigestDTO | undefined = digests[0];

  // Stop polling once a fresh digest (different id) lands.
  useEffect(() => {
    if (!awaitingRefresh) return;
    if (latest && latest.id !== baselineId.current) {
      setAwaitingRefresh(false);
      if (giveUp.current) clearTimeout(giveUp.current);
      toast.success("Highlight terbaru sudah siap");
    }
  }, [awaitingRefresh, latest, toast]);

  const refresh = useMutation({
    mutationFn: () => {
      if (!accountKey) throw new Error("Pilih brand terlebih dahulu");
      return api.refreshHighlights(accountKey);
    },
    onSuccess: () => {
      baselineId.current = latest?.id ?? null;
      setAwaitingRefresh(true);
      toast.success("Mengambil highlight terbaru… (±30–60 detik)");
      if (giveUp.current) clearTimeout(giveUp.current);
      giveUp.current = setTimeout(() => setAwaitingRefresh(false), 90000);
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Refresh failed"),
  });

  const generate = useMutation({
    mutationFn: ({
      digestId,
      itemIndex,
    }: {
      digestId: string;
      itemIndex: number;
    }) => api.generateFromHighlight(digestId, itemIndex, imageSource),
    onSuccess: () => {
      toast.success("Membuat draft… akan muncul di daftar bawah (±30 detik)");
      // The draft is produced asynchronously (claude + render); refetch the
      // drafts list a few times as it lands.
      [6000, 15000, 30000].forEach((ms) =>
        setTimeout(
          () => void queryClient.invalidateQueries({ queryKey: ["drafts"] }),
          ms,
        ),
      );
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Generation failed"),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-navy">
            Special Case
          </h1>
          <p className="text-sm text-slate-500">
            Reactive content from today&apos;s relevant highlights — you choose
            what to generate.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="w-44">
            <ImageSourceSelect
              id="special-image-source"
              value={imageSource}
              onChange={setImageSource}
              disabled={generate.isPending}
            />
          </div>
          <Button
            variant="primary"
            onClick={() => refresh.mutate()}
            disabled={!accountKey || refresh.isPending}
          >
            {refresh.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh highlights
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Latest digest</CardTitle>
          <CardDescription>
            {latest
              ? `Generated ${formatDateTime(latest.createdAt)} · ${latest.items.length} highlight${latest.items.length === 1 ? "" : "s"}`
              : "No digest yet for this account."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {awaitingRefresh ? (
            <div className="flex items-center gap-2 rounded-md bg-teal/10 px-3 py-2 text-sm font-medium text-teal">
              <Loader2 className="h-4 w-4 animate-spin" />
              Mengambil highlight terbaru… (±30–60 detik). Daftar akan otomatis
              diperbarui.
            </div>
          ) : null}
          {digestsQuery.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-teal" />
            </div>
          ) : digestsQuery.isError ? (
            <p className="text-sm text-coral">Failed to load highlights.</p>
          ) : !latest || latest.items.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">
              No highlights yet. Use &ldquo;Refresh highlights&rdquo; to run
              today&apos;s research.
            </p>
          ) : (
            latest.items.map((item, index) => (
              <HighlightRow
                key={`${latest.id}-${index}`}
                item={item}
                onGenerate={() =>
                  generate.mutate({ digestId: latest.id, itemIndex: index })
                }
                generating={
                  generate.isPending &&
                  generate.variables?.digestId === latest.id &&
                  generate.variables?.itemIndex === index
                }
                disabled={generate.isPending}
              />
            ))
          )}
        </CardContent>
      </Card>

      <DraftsList mode="special" />
    </div>
  );
}

function HighlightRow({
  item,
  onGenerate,
  generating,
  disabled,
}: {
  item: HighlightItem;
  onGenerate: () => void;
  generating: boolean;
  disabled: boolean;
}) {
  const sensitive = item.riskFlag === "sensitif";
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-slate-200 p-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="navy">{formatScore(item.relevanceScore)}</Badge>
          {sensitive ? (
            <Badge variant="coral">
              <AlertTriangle className="mr-1 h-3 w-3" />
              Sensitif
            </Badge>
          ) : (
            <Badge variant="green">
              <ShieldCheck className="mr-1 h-3 w-3" />
              Aman
            </Badge>
          )}
        </div>
        <p className="font-medium text-slate-900">{item.title}</p>
        <p className="text-sm text-slate-600">{item.summary}</p>
        {item.reason ? (
          <p className="text-xs italic text-slate-500">{item.reason}</p>
        ) : null}
        {item.url ? (
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-teal hover:underline"
          >
            <ExternalLink className="h-3 w-3" />
            Source
          </a>
        ) : null}
      </div>
      <div className="shrink-0">
        <Button
          variant="outline"
          size="sm"
          onClick={onGenerate}
          disabled={disabled}
        >
          {generating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          Generate
        </Button>
      </div>
    </div>
  );
}
